import { type IAssignedAgent } from '../../../interface/agent-assignment';

/** Up-to-two-letter initials for the avatar fallback (no photo field exists). */
export const initials = (firstName?: string, lastName?: string, userName?: string): string => {
	const f = (firstName || '').trim();
	const l = (lastName || '').trim();
	if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || f.slice(0, 2).toUpperCase();
	const u = (userName || '').trim();
	return u ? u.slice(0, 2).toUpperCase() : '—';
};

/** Best human display name for an assigned agent. */
export const agentDisplayName = (agent: IAssignedAgent | null | undefined): string => {
	if (!agent) return 'Unknown agent';
	const full = `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim();
	return full || agent.userName || 'Unknown agent';
};

/** First non-administrator role name, defaulting to "Party Agent". */
export const agentRoleLabel = (agent: IAssignedAgent | null | undefined): string => {
	const role = agent?.roles?.find((r) => !r.isAdministratorRole) || agent?.roles?.[0];
	return role?.name || 'Party Agent';
};

/**
 * Normalise a Nigerian phone number for a wa.me deep link: keep digits only and
 * convert a leading 0 to the 234 country code. Returns '' when there's nothing usable.
 */
export const formatPhoneForWa = (phone?: string): string => {
	const digits = (phone || '').replace(/\D+/g, '');
	if (!digits) return '';
	if (digits.startsWith('234')) return digits;
	if (digits.startsWith('0')) return `234${digits.slice(1)}`;
	return digits;
};

/** Deterministic avatar background tint from a seed (entity code / name). */
export const avatarTint = (seed: string): string => {
	let hash = 0;
	for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 360;
	const hue = Math.abs(hash);
	return `hsl(${hue}, 52%, 42%)`;
};

/** Human-friendly "uploaded on" label from an ISO date, or null. */
export const formatUploadedAt = (iso?: string | null): string | null => {
	if (!iso || iso.startsWith('0001')) return null;
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/** Coerce a JSON boolean OR a "true"/"false" string into a real boolean. */
export const asBool = (value?: boolean | string | null): boolean => value === true || value === 'true';

/** Compact "x min ago" / "just now" label from an ISO timestamp, or null. */
export const relativeTime = (iso?: string | null): string | null => {
	if (!iso) return null;
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return null;
	const diffMs = Date.now() - then;
	const min = Math.round(diffMs / 60000);
	if (min < 1) return 'just now';
	if (min < 60) return `${min} min ago`;
	const hrs = Math.round(min / 60);
	if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
	const days = Math.round(hrs / 24);
	return `${days} day${days > 1 ? 's' : ''} ago`;
};

/** Format a number with thousands separators, or an em-dash for nullish/NaN. */
export const fmtNum = (value?: number | null): string => (value == null || Number.isNaN(value) ? '—' : value.toLocaleString());
